// client/src/components/leagues/LeagueMatches.js
import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import PageLayout from '../common/layout/PageLayout'
import ApiMatches from '../../api/ApiMatches'
import { COMPLETED_STATUSES } from '../../utils/matchStatus'
import { formatMatchScore, getMatchSidePlayers } from '../../utils/match'
import { formatDateTime } from '../../utils/format'
import './LeagueMatches.css'

// Build a trimmed full name from possibly null/undefined parts
function concatName(first, last) {
  return `${first || ''} ${last || ''}`.trim()
}

// Display helper: prefer full name; fallback to Player #<id>
function getPlayerDisplayName(first, last, id) {
  const full = concatName(first, last)
  return full || (id ? `Player #${id}` : 'Player')
}

// Sort helper: use lowercase name when present; otherwise sort by ID string (numeric aware)
function getSortKeyForPlayer(first, last, id) {
  const full = concatName(first, last)
  return full ? full.toLowerCase() : String(id ?? '')
}

function formatTeamDisplayName(players) {
  const names = (players || [])
    .map((player) => concatName(player?.first_name, player?.last_name))
    .filter(Boolean)

  if (names.length > 0) {
    return names.join(' / ')
  }

  return ''
}

function getSideTeamDisplayName(match, side) {
  return formatTeamDisplayName(getMatchSidePlayers(match, side))
}

function getSideDisplayName(match, side) {
  const teamDisplayName = getSideTeamDisplayName(match, side)
  if (teamDisplayName) return teamDisplayName

  return side === 'home'
    ? getPlayerDisplayName(
        match.home_first_name,
        match.home_last_name,
        match.home_player_id,
      )
    : getPlayerDisplayName(
        match.away_first_name,
        match.away_last_name,
        match.away_player_id,
      )
}

function getSortKeyForSide(match, side) {
  const teamDisplayName = getSideTeamDisplayName(match, side)
  if (teamDisplayName) return teamDisplayName.toLowerCase()

  return side === 'home'
    ? getSortKeyForPlayer(
        match.home_first_name,
        match.home_last_name,
        match.home_player_id,
      )
    : getSortKeyForPlayer(
        match.away_first_name,
        match.away_last_name,
        match.away_player_id,
      )
}

// Identifies matches whose regular season ended without being played.
// Extracted to module scope so filterAndSortMatches and totalCount share
// one definition and stay in sync if the criteria ever changes.
const isRegularSeasonUnplayed = (match) =>
  match.status === 'unplayed' && (match.type || 'regular') === 'regular'

// Explicit helper: always format scores as Home-Away
// This avoids ambiguity about user orientation in this list view.
function formatScoreHomeAway(match) {
  return formatMatchScore(match, match.home_player_id)
}

export default function LeagueMatches() {
  const { leagueId } = useParams()
  const navigate = useNavigate()

  const [loading, setLoading] = useState(true)
  const [matches, setMatches] = useState([])
  const [filteredMatches, setFilteredMatches] = useState([])
  const [error, setError] = useState('')
  const [divisionFilter, setDivisionFilter] = useState('all') // 'all' or division_id
  const [statusFilter, setStatusFilter] = useState('all') // 'all', 'completed', 'scheduled', 'unscheduled'
  const [sortBy, setSortBy] = useState('date-desc') // 'date-desc', 'date-asc', 'players'
  const [divisions, setDivisions] = useState([]) // List of divisions in the league

  const loadMatches = useCallback(async () => {
    try {
      setLoading(true)
      setError('')

      const response = await ApiMatches.getByLeague(leagueId)
      // ApiMatches.getByLeague returns an object like { matches: [...] }
      // but be defensive in case of different shapes
      const matchesList = Array.isArray(response)
        ? response
        : Array.isArray(response?.matches)
          ? response.matches
          : Array.isArray(response?.data?.matches)
            ? response.data.matches
            : null

      if (matchesList) {
        setMatches(matchesList)

        // Extract unique divisions from matches
        const uniqueDivisions = new Map()
        matchesList.forEach((match) => {
          if (match.division_id && match.division_name) {
            uniqueDivisions.set(match.division_id, match.division_name)
          }
        })

        // Convert to array and sort by division name
        const divisionsArray = Array.from(uniqueDivisions.entries())
          .map(([id, name]) => ({ id, name }))
          .sort((a, b) => a.name.localeCompare(b.name))

        setDivisions(divisionsArray)
      } else {
        setError('Failed to load matches')
      }
    } catch (err) {
      console.error('Error loading matches:', err)
      setError('Failed to load league matches. Please try again later.')
    } finally {
      setLoading(false)
    }
  }, [leagueId])

  const filterAndSortMatches = useCallback(() => {
    let filtered = matches

    // Apply division filter first
    if (divisionFilter !== 'all') {
      filtered = filtered.filter(
        (match) => match.division_id === parseInt(divisionFilter),
      )
    }

    // Apply status filters
    // Always filter out 'created' matches - these are unscheduled placeholders
    if (statusFilter === 'completed') {
      filtered = filtered.filter(
        (match) =>
          COMPLETED_STATUSES.has(match.status) ||
          isRegularSeasonUnplayed(match),
      )
    } else if (statusFilter === 'scheduled') {
      filtered = filtered.filter((match) => match.status === 'scheduled')
    } else if (statusFilter === 'unscheduled') {
      // Unscheduled = created status (not yet scheduled)
      filtered = filtered.filter((match) => match.status === 'created')
    } else {
      // 'all' - exclude 'created' status to only show meaningful matches
      filtered = filtered.filter(
        (match) =>
          match.status !== 'created' && !isRegularSeasonUnplayed(match),
      )
    }

    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'date-asc':
          // For completed matches use updated_at; otherwise use match_datetime
          return (
            new Date(
              (COMPLETED_STATUSES.has(a.status)
                ? a.updated_at
                : a.match_datetime) || 0,
            ) -
            new Date(
              (COMPLETED_STATUSES.has(b.status)
                ? b.updated_at
                : b.match_datetime) || 0,
            )
          )
        case 'date-desc':
          return (
            new Date(
              (COMPLETED_STATUSES.has(b.status)
                ? b.updated_at
                : b.match_datetime) || 0,
            ) -
            new Date(
              (COMPLETED_STATUSES.has(a.status)
                ? a.updated_at
                : a.match_datetime) || 0,
            )
          )
        case 'players': {
          const keyA = getSortKeyForSide(a, 'home')
          const keyB = getSortKeyForSide(b, 'home')
          const cmp = keyA.localeCompare(keyB, undefined, {
            numeric: true,
            sensitivity: 'base',
          })
          if (cmp !== 0) return cmp
          // Tiebreak by home player id to stabilize sort
          return (a.home_player_id || 0) - (b.home_player_id || 0)
        }
        default:
          return 0
      }
    })

    setFilteredMatches(filtered)
  }, [matches, divisionFilter, statusFilter, sortBy])

  useEffect(() => {
    loadMatches()
  }, [loadMatches])

  useEffect(() => {
    filterAndSortMatches()
  }, [filterAndSortMatches])

  const getMatchStatusDisplay = (match) => {
    switch (match.status) {
      case 'normal':
        return { label: 'Completed', className: 'completed', icon: '✅' }
      case 'home_retired':
      case 'away_retired':
      case 'i_retired':
      case 'opponent_retired':
        return { label: 'Retired', className: 'retired', icon: '⚠️' }
      case 'created':
        return { label: 'Unscheduled', className: 'unscheduled', icon: '📝' }
      case 'scheduled':
        return { label: 'Scheduled', className: 'scheduled', icon: '📅' }
      case 'unplayed':
        return { label: 'Not played', className: 'unscheduled', icon: '—' }
      default:
        return { label: 'Unknown', className: 'unknown', icon: '❓' }
    }
  }

  const getMatchScore = (match) => {
    if (match.status === 'unplayed') {
      return 'Not played'
    }

    if (!COMPLETED_STATUSES.has(match.status)) {
      return 'Not completed'
    }

    // Intentionally show score from home player's perspective for consistency
    // with the Home/Away labels in this grid (viewer context is unknown here)
    return formatScoreHomeAway(match)
  }

  const handleMatchClick = (match) => {
    // Navigate to match score entry/details page if available
    navigate(`/leagues/${leagueId}/match/${match.id}/score`)
  }

  if (loading) {
    return (
      <PageLayout title="League Matches">
        <div className="league-matches-container">
          <div className="loading-container">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
            <p className="mt-3">Loading league matches...</p>
          </div>
        </div>
      </PageLayout>
    )
  }

  if (error) {
    return (
      <PageLayout title="League Matches">
        <div className="league-matches-container">
          <div className="alert alert-danger">
            <h5>Error Loading Matches</h5>
            <p>{error}</p>
            <button className="btn btn-primary" onClick={loadMatches}>
              Try Again
            </button>
          </div>
        </div>
      </PageLayout>
    )
  }

  const completedCount = matches.filter((match) =>
    COMPLETED_STATUSES.has(match.status),
  ).length
  const unplayedCount = matches.filter(isRegularSeasonUnplayed).length
  const scheduledCount = matches.filter(
    (match) => match.status === 'scheduled',
  ).length
  const unscheduledCount = matches.filter(
    (match) => match.status === 'created',
  ).length
  const totalCount = matches.filter(
    (match) => match.status !== 'created' && !isRegularSeasonUnplayed(match),
  ).length // Exclude unscheduled and regular-season unplayed from total count

  return (
    <PageLayout title="League Matches">
      <div className="league-matches-container">
        {/* Header */}
        <div className="matches-header">
          <div className="header-content">
            <div className="title-section">
              <h2>League Matches</h2>
              <p className="text-muted">
                {completedCount} of {totalCount} matches completed
              </p>
            </div>
            <div className="header-actions">
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => navigate(`/leagues/${leagueId}`)}
              >
                ← Back to League
              </button>
            </div>
          </div>

          <div className="controls-section">
            {/* Division Filter */}
            {divisions.length > 1 && (
              <div className="filter-controls">
                <label className="form-label" htmlFor="division-filter">
                  Division:
                </label>
                <select
                  id="division-filter"
                  className="form-select form-select-sm"
                  value={divisionFilter}
                  onChange={(e) => setDivisionFilter(e.target.value)}
                  aria-label="Filter by division"
                >
                  <option value="all">All Divisions</option>
                  {divisions.map((division) => (
                    <option key={division.id} value={division.id}>
                      {division.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Status Filter */}
            <div className="filter-controls">
              <label className="form-label" htmlFor="match-filter">
                Status:
              </label>
              <div
                className="btn-group"
                role="group"
                aria-label="Filter matches by status"
                id="match-filter"
              >
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === 'all'
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => setStatusFilter('all')}
                  aria-pressed={statusFilter === 'all'}
                >
                  Scheduled & Completed ({totalCount})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === 'completed'
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => setStatusFilter('completed')}
                  aria-pressed={statusFilter === 'completed'}
                >
                  Completed / Not Played ({completedCount + unplayedCount})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === 'scheduled'
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => setStatusFilter('scheduled')}
                  aria-pressed={statusFilter === 'scheduled'}
                >
                  Scheduled ({scheduledCount})
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${
                    statusFilter === 'unscheduled'
                      ? 'btn-primary'
                      : 'btn-outline-primary'
                  }`}
                  onClick={() => setStatusFilter('unscheduled')}
                  aria-pressed={statusFilter === 'unscheduled'}
                >
                  Unscheduled ({unscheduledCount})
                </button>
              </div>
            </div>

            <div className="sort-controls">
              <label className="form-label" htmlFor="match-sort">
                Sort:
              </label>
              <select
                id="match-sort"
                className="form-select form-select-sm"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort matches"
              >
                <option value="date-desc">Newest First</option>
                <option value="date-asc">Oldest First</option>
                <option value="players">By Players</option>
              </select>
            </div>
          </div>
        </div>

        {/* Matches List */}
        {filteredMatches.length === 0 ? (
          <div className="no-matches-message" role="status" aria-live="polite">
            <div className="empty-icon" aria-hidden="true">
              🎾
            </div>
            <h3>No Matches Found</h3>
            <p>
              {statusFilter === 'all'
                ? divisionFilter === 'all'
                  ? 'No matches have been created for this league yet.'
                  : 'No matches found for this division.'
                : statusFilter === 'completed'
                  ? 'No completed matches found. Matches will appear here once they are finished.'
                  : statusFilter === 'scheduled'
                    ? 'No scheduled matches found. Schedule matches to see them here.'
                    : 'No unscheduled matches found.'}
            </p>
            <div className="action-buttons">
              <button
                className="btn btn-primary"
                onClick={() => navigate(`/leagues/${leagueId}`)}
              >
                Back to League
              </button>
            </div>
          </div>
        ) : (
          <div className="matches-grid" role="list" aria-label="Match list">
            {filteredMatches.map((match) => {
              const status = getMatchStatusDisplay(match)
              const matchScore = getMatchScore(match)
              // Display updated_at for completed matches as proxy for completion time; otherwise show scheduled date
              const matchDate = COMPLETED_STATUSES.has(match.status)
                ? match.updated_at || match.match_datetime
                : match.match_datetime || match.updated_at

              const homePlayerName = getSideDisplayName(match, 'home')
              const awayPlayerName = getSideDisplayName(match, 'away')
              return (
                <div
                  key={match.id}
                  className="matches-grid__item"
                  role="listitem"
                >
                  <div
                    className="match-card"
                    onClick={() => handleMatchClick(match)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        handleMatchClick(match)
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Match between ${homePlayerName} and ${awayPlayerName}. Status: ${status.label}. ${COMPLETED_STATUSES.has(match.status) ? `Score: ${matchScore}` : ''}`}
                  >
                    <div className="match-header">
                      <div className="match-status">
                        <span
                          className={`status-badge ${status.className}`}
                          aria-label={`Match status: ${status.label}`}
                        >
                          <span aria-hidden="true">{status.icon}</span>{' '}
                          {status.label}
                        </span>
                      </div>
                      <div className="match-date">
                        {matchDate &&
                          formatDateTime(matchDate, {
                            includeYear: false,
                            includeTime: false,
                          })}
                      </div>
                    </div>

                    <div className="match-players">
                      <div className="player home-player">
                        <div className="player-name">{homePlayerName}</div>
                        <div className="player-label">Home</div>
                      </div>

                      <div className="match-vs">
                        <div className="vs-label" aria-hidden="true">
                          VS
                        </div>
                        <div
                          className="match-score"
                          aria-label={`Match score: ${matchScore}`}
                        >
                          {matchScore}
                        </div>
                      </div>

                      <div className="player away-player">
                        <div className="player-name">{awayPlayerName}</div>
                        <div className="player-label">Away</div>
                      </div>
                    </div>

                    {match.home_court_name && (
                      <div
                        className="match-court"
                        aria-label={`Court: ${match.home_court_name}${match.home_court_city ? ', ' + match.home_court_city : ''}`}
                      >
                        <span className="court-icon" aria-hidden="true">
                          🏟️
                        </span>
                        <span className="court-name">
                          {match.home_court_name}
                        </span>
                        {match.home_court_city && (
                          <span className="court-location">
                            , {match.home_court_city}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Statistics Summary */}
        {totalCount > 0 && (
          <div className="statistics-section">
            <h5>📊 Match Statistics</h5>
            <div className="stats-grid">
              <div className="stat-item">
                <div className="stat-value">{totalCount}</div>
                <div className="stat-label">Total Matches</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">{completedCount}</div>
                <div className="stat-label">Completed</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">
                  {completedCount > 0
                    ? Math.round((completedCount / totalCount) * 100)
                    : 0}
                  %
                </div>
                <div className="stat-label">Completion Rate</div>
              </div>
              <div className="stat-item">
                <div className="stat-value">
                  {matches.filter((m) => m.status === 'scheduled').length}
                </div>
                <div className="stat-label">Scheduled</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageLayout>
  )
}
